import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  FormControl,
  HStack,
  Image,
  Radio,
  Spinner,
  Text,
  Textarea,
  useCheckboxGroup,
  useRadioGroup,
  useToast,
  VStack
} from '@chakra-ui/react';
import { commentApi } from '../../../../../../apis/comment.api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import CloseModalButton from '../../../../../../assets/close-modal-icon.svg';
import ToastBtnMessage from '../../../../../../components/ToastBtnMessage';
import { moderationApi } from '../../../../../../apis/moderation.api';
import { ignoreArticle } from '../../../../../../apis/article.api';
import { ModerationType, ResourceType } from '../../../../../../types/moderation.types';
import { formatViolationType } from '../../../../../../utils/helper.util';
import { capitalize } from 'lodash-es';
import DataLayer from '../../../../../../utils/data-layer';
import { UnauthorizedDispatcher } from 'src/utils/unauthorized-dispatcher';

const ReportSidePanel = ({
  article,
  targetType,
  openReportModal,
  setOpenReportModal,
  handleIgnoreArticle
}) => {
  const [note, setNote] = useState('');
  const [isIgnoreWriter, setIsIgnoreWriter] = useState(false);
  const textareaRef = useRef(null);
  const toast = useToast();
  const { getCheckboxProps, value: selectedOptions } = useCheckboxGroup();
  const [selectedViolations, setSelectedViolations] = useState<ModerationType[]>([]);
  const userId = JSON.parse(localStorage.getItem('user'))?.id;

  const handleViolationToggle = (violation: ModerationType) => {
    setSelectedViolations((current) =>
      current.includes(violation) ? current.filter((v) => v !== violation) : [...current, violation]
    );
  };
  const {
    getRootProps,
    getRadioProps,
    value: radioValue
  } = useRadioGroup({
    defaultValue: 'none'
  });
  const group = getRootProps();
  const isError = selectedOptions.includes('14');

  const ignoreOptions = [
    {
      value: 'ignoreWriter',
      label: 'Ignore the writer of this article',
      subtext: "Don't show content from this writer again."
    },
    {
      value: 'ignoreArticle',
      label: 'Ignore the article only',
      subtext: "Don't show this article again."
    }
  ];

  const maxLength = 2000;

  const queryClient = useQueryClient();

  // const { data: reportOptions = [], isFetching } = useQuery({
  //   queryKey: ['reports'],
  //   queryFn: commentApi.getReportOptions,
  //   staleTime: 10000
  // });

  const { data: violationTypes = [], isFetching } = useQuery({
    queryKey: ['violationTypes'],
    queryFn: moderationApi.getViolationTypes,
    staleTime: 10000
  });

  const { mutateAsync: submitReport, isPending: isReportPending } = useMutation({
    mutationFn: moderationApi.createReport,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    }
  });

  const { mutateAsync: blockUser } = useMutation({
    mutationFn: commentApi.blockUser,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['block']
      });
    }
  });

  const handleReport = async () => {
    try {
      if (!userId) return UnauthorizedDispatcher('required');

      const isArticle = targetType === 'ARTICLE';
      const targetId = isArticle ? article.id : article.creatorId;

      // Submit report to moderation service
      await submitReport({
        targetId,
        targetType: targetType as ResourceType,
        violations: selectedViolations,
        detail: note
      });

      // Track report event when reporting
      if (targetType === ResourceType.USER && article?.creator?.username) {
        // Track writer report event
        DataLayer.trackWriterReport(article.creator.username);
      } else if (targetType === ResourceType.ARTICLE) {
        // Track article report event
        const articleType = article?.writingType?.name;
        DataLayer.trackArticleReport(articleType, article.id);
      }

      // Handle ignore/block actions in content service
      if (isArticle) {
        if (radioValue === 'ignoreArticle') {
          // Track ignore article
          const articleType = article?.writingType?.name;
          DataLayer.trackArticleIgnore(articleType, article.id);
          await ignoreArticle(article.id);
        } else if (radioValue === 'ignoreWriter') {
          // Track writer ignore event
          if (article?.creator?.username) {
            DataLayer.trackWriterIgnore(article.creator.username);
          }
          await blockUser(article.creatorId);
        }
      } else if (isIgnoreWriter) {
        // Track writer ignore event
        if (article?.creator?.username) {
          DataLayer.trackWriterIgnore(article.creator.username);
        }
        await blockUser(article.creatorId);
      }

      toast({
        render: () => (
          <ToastBtnMessage
            title={
              isIgnoreWriter || radioValue === 'ignoreWriter' || radioValue === 'ignoreArticle'
                ? `${capitalize(targetType.toLowerCase())} reported, you won't see it again.`
                : `Successfully reported the ${capitalize(targetType.toLowerCase())}`
            }
            isError={false}
            color="jet-black"
            isHiddenButton
          />
        ),
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'top-right'
      });

      setOpenReportModal(false);
    } catch (error) {
      console.error('Failed to submit report:', error);
      toast({
        render: () => (
          <ToastBtnMessage
            title={error?.response?.data?.message}
            isError={true}
            color="red"
            isHiddenButton
          />
        ),
        status: 'error',
        duration: 2000,
        isClosable: true,
        position: 'top-right'
      });
    }
  };

  const handleChangeNote = (e) => {
    adjustHeight();
    setNote(e.target.value);
  };

  const adjustHeight = () => {
    textareaRef.current.style.height = '';
    const newHeight = Math.min(textareaRef.current.scrollHeight + 1, 150); // 32 55 78
    textareaRef.current.style.height = `${newHeight}px`;
  };

  useEffect(() => {
    if (textareaRef.current) {
      adjustHeight();
    }
  });

  return (
    <Drawer
      isOpen={openReportModal}
      placement="right"
      onClose={() => setOpenReportModal(false)}
      size="xl"
    >
      <DrawerOverlay />
      <Box overflowY={'auto'} position={'relative'}>
        <DrawerContent maxW={'653px'} pb="env(safe-area-inset-bottom)">
          <DrawerBody p={0} overflowY={'auto'}>
            <VStack
              p={6}
              gap={6}
              alignItems={'start'}
              w="100%"
              h="100%"
              position={'relative'}
              bg={'white'}
              overflow={'auto'}
            >
              <HStack
                justifyContent="space-between"
                alignItems={'center'}
                pb={2}
                borderBottom={'1px solid #D1C8C4'}
                w={'100%'}
                bg={'white'}
              >
                <Text fontSize="20px" fontWeight={800}>
                  Report {targetType === ResourceType.ARTICLE ? 'Article' : 'Writer'}
                </Text>
                <Image
                  src={CloseModalButton}
                  cursor={'pointer'}
                  _hover={{ opacity: '0.8' }}
                  onClick={() => setOpenReportModal(false)}
                />
              </HStack>
              <Box
                w="100%"
                display={'flex'}
                flexDir={'column'}
                gap={6}
                alignItems={'start'}
                overflowY={'auto'}
                pr={4}
              >
                <Box w={'100%'}>
                  <Text mb={4}>
                    Please select the issue(s) that apply. You can choose one or more violations.
                  </Text>

                  <VStack spacing={4} align="start">
                    {!isFetching ? (
                      violationTypes.map((violationType) => {
                        const isSelected = selectedViolations.includes(violationType.type);
                        return (
                          <Box key={violationType.type}>
                            <Checkbox
                              isChecked={isSelected}
                              onChange={() => handleViolationToggle(violationType.type)}
                            >
                              <Text fontWeight={isSelected ? '800' : '600'}>
                                {formatViolationType(violationType.type)}
                              </Text>
                            </Checkbox>
                            <Text fontWeight={400} pl={'28px'}>
                              {violationType.description}
                            </Text>
                          </Box>
                        );
                      })
                    ) : (
                      <Spinner my={'24px'} alignSelf={'center'} size={'md'} color="green.500" />
                    )}
                  </VStack>
                </Box>

                <Box w={'100%'}>
                  <Text mb={2} fontWeight={600}>
                    Notes
                  </Text>
                  <FormControl isRequired={isError}>
                    <Textarea
                      value={note}
                      ref={textareaRef}
                      onChange={handleChangeNote}
                      placeholder="Write any additional notes here"
                      p={{ base: '12px 12px', '2xl': '17px 24px' }}
                      fontSize={{ base: 'sm', '2xl': 'md' }}
                      rounded={'0.75rem'}
                      minHeight={'unset'}
                      resize={'none'}
                      borderWidth={'1px'}
                      borderColor={'light-gray'}
                      color={'jet-black'}
                      bg={'#FAFAFA'}
                      lineHeight={'1.25rem'}
                      _hover={{
                        borderColor: 'inherit'
                      }}
                      _focus={{
                        boxShadow: 'none'
                      }}
                      _focusVisible={{
                        borderColor: 'twygs-green',
                        borderWidth: '1.5px'
                      }}
                      css={{
                        '&::-webkit-scrollbar': {
                          display: 'none'
                        }
                      }}
                      maxLength={maxLength}
                      mb={2}
                    />
                    <Flex justify="end" fontSize="sm" color="gray.500">
                      <Text fontSize={'sm'} color={'dim-gray'}>
                        {note.length}/{maxLength.toLocaleString()}
                      </Text>
                    </Flex>
                  </FormControl>
                </Box>
                {targetType === ResourceType.ARTICLE ? (
                  <Box w={'100%'}>
                    <Text mb={4}>Do you want to ignore this article or its writer?</Text>
                    <VStack {...group} spacing={3} align="start" mb={6}>
                      {ignoreOptions.map((option) => {
                        const radio = getRadioProps({ value: option.value });
                        return (
                          <Box key={option.value}>
                            <Radio {...(radio as any)}>
                              <Text fontWeight={radioValue === option.value ? '800' : '600'}>
                                {option.label}
                              </Text>
                            </Radio>
                            <Text fontWeight={400} pl={'28px'}>
                              {option.subtext}
                            </Text>
                          </Box>
                        );
                      })}
                    </VStack>
                  </Box>
                ) : (
                  <Box w={'100%'}>
                    <Text mb={4}>Do you want to ignore this writer?</Text>

                    <VStack spacing={3} align="start" mb={6}>
                      <Box>
                        <Checkbox onChange={() => setIsIgnoreWriter(!isIgnoreWriter)}>
                          <Text fontWeight={isIgnoreWriter ? '800' : '600'}>
                            Ignore the writer of this article
                          </Text>
                        </Checkbox>
                        <Text fontWeight={400} pl={'28px'}>
                          Don't show content from this writer again.
                        </Text>
                      </Box>
                    </VStack>
                  </Box>
                )}
              </Box>

              <HStack w={'100%'} justifyContent={'end'}>
                <Button
                  bg={'white'}
                  border={'2px solid'}
                  borderColor={'growth-green'}
                  rounded={'8px'}
                  px={'18px'}
                  w={'fit-content'}
                  onClick={() => setOpenReportModal(false)}
                  h={'42px'}
                  color={'growth-green'}
                  fontWeight={900}
                  fontSize={'md'}
                  _hover={{ opacity: '0.7' }}
                >
                  Cancel
                </Button>
                <Button
                  bg={'growth-green'}
                  color={'white'}
                  rounded={'8px'}
                  w={'fit-content'}
                  h={'42px'}
                  px={'18px'}
                  onClick={handleReport}
                  fontWeight={900}
                  fontSize={'md'}
                  _hover={{ opacity: '0.7' }}
                  _disabled={{
                    opacity: '0.4',
                    cursor: 'not-allowed',
                    bg: 'white',
                    color: 'dim-gray',
                    borderColor: 'light-gray'
                  }}
                  isLoading={isReportPending}
                  isDisabled={
                    selectedViolations.length === 0 ||
                    radioValue === '' ||
                    (isError && note.trim().length === 0)
                  }
                >
                  Report
                </Button>
              </HStack>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Box>
    </Drawer>
  );
};

export default ReportSidePanel;
